import { FC, Fragment, ReactNode, useContext } from "react";
import {
  createStyles,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  useMediaQuery,
} from "@material-ui/core";
import moment from "moment";

import { AppContext } from "contexts";
import { useTimer } from "hooks";
import { calculateTermRemaining } from "lib/date";
import {
  formatAmount,
  formatCurrencyAmount,
  formatDateRange,
  formatHumanDate,
  formatMarketplaceMedian,
  formatMarketplaceMedianAmountSource,
  formatNumber,
} from "lib/format";
import { getTermDisplayName, singularOrPlural } from "lib/helpers";
import { parseNumber } from "lib/parse";
import { Asset } from "services/listings/types";
import { ListingValuation, ListingWithIncludes } from "services/listings/types";
import theme from "theme";

const useStyles = makeStyles(() =>
  createStyles({
    tableContainer: {
      backgroundColor: theme.custom.palette.gray.light,
      borderRadius: theme.shape.borderRadius,
      boxShadow: theme.shadows[3],
    },
    tableCell: {
      padding: theme.spacing(1),
      [theme.breakpoints.down("xs")]: {
        fontSize: "0.8125rem",
      },
    },
    removeBorderBottom: {
      borderBottom: "none",
    },
    rightCell: {
      fontWeight: theme.typography.fontWeightMedium,
      textAlign: "right",
    },
    removePaddingLeft: {
      paddingLeft: 0,
    },
    removePaddingRight: {
      paddingRight: 0,
    },
    tableLink: {
      color: theme.custom.palette.blue.main,
      fontWeight: "inherit",
      fontSize: "inherit",
      fontFamily: "inherit",
      "&:hover": {
        textDecoration: "underline",
      },
    },
  }),
);

type RowContent = {
  label: string;
  value: ReactNode;
};

type Section = RowContent[];

interface AboutTableProps {
  asset: Asset;
  listing: ListingWithIncludes | undefined;
  valuation: ListingValuation;
  isModal: boolean;
}

const AboutTable: FC<AboutTableProps> = ({
  asset,
  listing,
  valuation,
  isModal,
}) => {
  const classes = useStyles();
  const isXsDown = useMediaQuery(theme.breakpoints.down("xs"), { noSsr: true });
  const currentTime = useTimer();
  const { user } = useContext(AppContext);

  const { resale_info, sale_date } = asset;
  const {
    expiration,
    marketplace_median,
    marketplace_median_multiplier,
    marketplace_median_source,
    minimum_price,
    state: listingState,
    term,
    term_expiration,
    term_return_amount,
  } = listing || {};
  const canSeeMarketplaceMedian = !!user && !!marketplace_median;

  const {
    dollar_age,
    first_earnings_date,
    lifetime_amount,
    ltm,
    three_years_average,
    track_list,
  } = valuation;
  const trackCount = track_list?.length;
  const isCompactDateFormat = isXsDown || isModal;

  const earningsPeriodString = first_earnings_date
    ? `Earnings Since ${moment(first_earnings_date).format("YYYY")}`
    : "Lifetime Earnings";

  const resaleSection: Section[] = !!resale_info
    ? [
        [
          {
            label: "Last Transaction Price",
            value: formatCurrencyAmount(
              resale_info.currency,
              resale_info.purchased_amount,
            ),
          },
          {
            label: "Last Transaction Date",
            value: formatHumanDate(
              resale_info.purchased_date,
              isCompactDateFormat,
            ),
          },
        ],
      ]
    : [];

  const termRow = !!term
    ? [{ label: "Term", value: getTermDisplayName(term, isXsDown) }]
    : [];

  const termInfoRows: RowContent[] =
    term === "fixed_return"
      ? [
          {
            label: "Fixed-Return Amount",
            value: formatAmount(term_return_amount, 0),
          },
        ]
      : !!term_expiration
      ? [
          {
            label: "Years Remaining",
            value: formatNumber(
              calculateTermRemaining(term_expiration, sale_date),
            ),
          },
          {
            label: "Expiration Date",
            value: formatHumanDate(term_expiration, isCompactDateFormat),
          },
        ]
      : [];

  const sections: Section[] = [
    [
      { label: "Last 12 Months", value: formatAmount(ltm, 0) },
      { label: "3-Year Average", value: formatAmount(three_years_average, 0) },
      {
        label: earningsPeriodString,
        value: formatAmount(lifetime_amount, 0),
      },
      { label: "Dollar Age", value: `${formatNumber(dollar_age)} Years` },
      {
        label: `${singularOrPlural("Track", "Tracks", trackCount)} Included`,
        value: trackCount,
      },
    ],
    ...resaleSection,
    [...termRow, ...termInfoRows],
  ];

  if (expiration && listingState === "open") {
    const isExpired = moment(currentTime).diff(expiration, "s") >= 0;
    const formattedExpiration = isExpired
      ? "Expired"
      : `${formatDateRange(currentTime, expiration, 1)} (${formatHumanDate(
          expiration,
          isCompactDateFormat,
        )})`;
    sections.push([
      {
        label: "Next Statement Update",
        value: formattedExpiration,
      },
    ]);
  }

  const priceRows = [];
  const marketplaceMedianFormatted = `${formatMarketplaceMedian(
    marketplace_median,
  )} (${formatNumber(
    Number(marketplace_median_multiplier),
  )} x ${formatMarketplaceMedianAmountSource(marketplace_median_source)})`;
  const marketplaceMedianPlaceholderFormatted = `${formatMarketplaceMedian(
    marketplace_median,
  )}`;
  priceRows.push({
    label: "Marketplace Median",
    value: `${
      canSeeMarketplaceMedian
        ? marketplaceMedianFormatted
        : marketplaceMedianPlaceholderFormatted
    }`,
  });

  const minimumPriceRow = {
    label: "Minimum Bid",
    value: formatCurrencyAmount("USD", minimum_price),
  };
  if (parseNumber(minimum_price) > 0) {
    priceRows.push(minimumPriceRow);
  }
  sections.push(priceRows);

  return (
    <TableContainer
      className={classes.tableContainer}
      data-testid="about-table"
    >
      <Table>
        <TableBody>
          {sections.map((section, sectionsIndex, sections) => (
            <Fragment key={`sections-${sectionsIndex}`}>
              {section.map(({ label, value }, sectionIndex, section) => {
                const lastOfSection = sectionIndex === section.length - 1;
                const lastSection = sectionsIndex === sections.length - 1;
                const borderBottomClass = `${
                  lastOfSection && !lastSection
                    ? ""
                    : classes.removeBorderBottom
                }`;
                return (
                  <TableRow key={label}>
                    <TableCell
                      className={`${classes.tableCell} ${borderBottomClass} ${classes.removePaddingRight}`}
                    >
                      {label}
                    </TableCell>
                    <TableCell
                      className={`${classes.tableCell} ${borderBottomClass} ${classes.rightCell} ${classes.removePaddingLeft}`}
                    >
                      {value}
                    </TableCell>
                  </TableRow>
                );
              })}
            </Fragment>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default AboutTable;
